import React, { useState, useMemo } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  User,
  Building2,
  Calendar,
  RefreshCw,
  Sparkles,
  Eye,
  Play,
  Database,
  Loader2,
  XCircle,
  Wallet,
  ExternalLink,
  X,
} from 'lucide-react';
import { useScreeningChecks, useRunScreening, useResolveHit, useHitSuggestion, useScreeningLists } from '../hooks/useScreening';
import { useToast } from '../contexts/ToastContext';
import { formatListSource } from '../utils/formatters';
import { Button, Badge, Card } from './ui';
import { cn } from '../lib/utils';

const statusConfig = {
  pending_review: { label: 'Pending Review', color: 'warning' },
  confirmed_clear: { label: 'Confirmed Clear', color: 'success' },
  confirmed_true: { label: 'True Positive', color: 'destructive' },
  clear: { label: 'Clear', color: 'success' },
};

const formatDateTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper to check if date is today
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// Country code to flag emoji (ISO 3166-1 alpha-2 to regional indicator)
const countryToFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const code = countryCode.toUpperCase();
  const offset = 127397; // Regional indicator symbol offset
  const flag = String.fromCodePoint(...[...code].map(c => c.charCodeAt(0) + offset));
  return flag;
};

// Country code to name mapping for display
const countryCodeToName = {
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  DE: 'Germany', FR: 'France', JP: 'Japan', KR: 'South Korea', IN: 'India',
  BR: 'Brazil', MX: 'Mexico', ZA: 'South Africa', AE: 'UAE', SA: 'Saudi Arabia',
  SG: 'Singapore', HK: 'Hong Kong', IL: 'Israel', NL: 'Netherlands', CH: 'Switzerland',
  IE: 'Ireland', RU: 'Russia', CN: 'China', IR: 'Iran', KP: 'North Korea',
  SY: 'Syria', CU: 'Cuba', VE: 'Venezuela', MM: 'Myanmar', BY: 'Belarus',
  UA: 'Ukraine', TR: 'Turkey', EG: 'Egypt', NG: 'Nigeria', PK: 'Pakistan',
};

// Modal components
function Modal({ isOpen, onClose, children }) {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[250]"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-2xl w-[500px]"
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// AI Suggestion Component for hit detail panel
function HitAISuggestion({ hitId, onResolve, isResolving }) {
  const { data: suggestion, isLoading, error } = useHitSuggestion(hitId);

  if (isLoading) {
    return (
      <Card className="border-grey-200 dark:border-grey-700 bg-grey-50 dark:bg-grey-800 p-4 opacity-70">
        <div className="flex items-center gap-2 font-semibold text-grey-500 mb-2 text-[13px]">
          <Loader2 size={16} className="animate-spin" />
          Analyzing hit...
        </div>
        <div className="text-[13px] text-grey-400">
          AI is reviewing this match against available data...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500/50 bg-red-50 dark:bg-red-950/20 p-4">
        <div className="flex items-center gap-2 font-semibold text-red-500 mb-2 text-[13px]">
          <XCircle size={16} />
          AI Review Unavailable
        </div>
        <div className="text-[13px] text-grey-600 dark:text-grey-400 mb-4">
          Unable to get AI recommendation. Please review manually.
        </div>
        <div className="flex gap-2">
          <Button
            variant="success"
            className="flex-1"
            onClick={() => onResolve('confirmed_false')}
            disabled={isResolving}
          >
            {isResolving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Mark as Clear
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => onResolve('confirmed_true')}
            disabled={isResolving}
          >
            {isResolving ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
            Confirm Match
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-teal-500/50 bg-gradient-to-br from-white to-teal-500/5 dark:from-grey-900 dark:to-teal-500/10 p-4">
      <div className="flex items-center gap-2 font-semibold text-teal-600 dark:text-teal-400 mb-3 text-[13px]">
        <Sparkles size={16} />
        AI Review Recommendation
      </div>
      <div className="text-sm leading-relaxed text-grey-900 dark:text-grey-50 mb-4">
        {suggestion?.reasoning || 'No AI analysis available for this hit.'}
      </div>
      {suggestion && (
        <div className="mb-4 text-sm">
          Suggested: <strong>{suggestion.suggested_resolution === 'confirmed_false' ? 'Clear (False Positive)' : 'True Match'}</strong>
          {suggestion.confidence && ` (${Math.round(suggestion.confidence * 100)}% confidence)`}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          variant="success"
          className="flex-1"
          onClick={() => onResolve('confirmed_false')}
          disabled={isResolving}
        >
          {isResolving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Mark as Clear
        </Button>
        <Button
          variant="destructive"
          className="flex-1"
          onClick={() => onResolve('confirmed_true')}
          disabled={isResolving}
        >
          {isResolving ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
          Confirm Match
        </Button>
      </div>
    </Card>
  );
}

// Loading Skeleton Component
function ScreeningSkeleton() {
  return (
    <div className="p-5">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="h-[60px] mb-3 rounded-lg bg-gradient-to-r from-grey-100 via-grey-50 to-grey-100 dark:from-grey-800 dark:via-grey-700 dark:to-grey-800 animate-pulse" />
      ))}
    </div>
  );
}

// Error State Component
function ErrorState({ message, onRetry }) {
  return (
    <Card className="text-center py-16 px-5">
      <XCircle size={48} className="text-red-500 mx-auto mb-4" />
      <h3 className="font-semibold mb-2">Failed to Load Screening Checks</h3>
      <p className="text-grey-500 mb-5">{message}</p>
      {onRetry && (
        <Button onClick={onRetry}>
          <RefreshCw size={14} />
          Try Again
        </Button>
      )}
    </Card>
  );
}

export default function ScreeningChecks() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showNewCheck, setShowNewCheck] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const toast = useToast();

  // Form state for new check modal
  const [formData, setFormData] = useState({
    entity_type: 'individual',
    name: '',
    country: '',
    date_of_birth: ''
  });

  // Map UI filter to API filter
  const apiFilter = useMemo(() => {
    if (activeFilter === 'hits') return { status: 'hit' };
    if (activeFilter === 'pending') return { status: 'pending_review' };
    return {};
  }, [activeFilter]);

  // Fetch screening checks from API
  const { data, isLoading, error, refetch } = useScreeningChecks(apiFilter);

  // Fetch list sources from API
  const { data: listSourcesData } = useScreeningLists();

  // Mutations
  const runScreeningMutation = useRunScreening();
  const resolveHitMutation = useResolveHit();

  // Calculate stats from real data
  const stats = useMemo(() => {
    const items = data?.items || [];
    return {
      pendingReview: items.filter(c => c.hits?.some(h => h.resolution_status === 'pending')).length,
      totalHits: items.filter(c => c.status === 'hit').length,
      truePositives: items.filter(c => c.hits?.some(h => h.resolution_status === 'confirmed_true')).length,
      checksToday: items.filter(c => isToday(c.started_at || c.created_at)).length,
    };
  }, [data]);

  const checks = data?.items || [];
  const listSources = listSourcesData?.items || [];

  // Handle running a new screening check
  const handleRunCheck = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a name to screen');
      return;
    }

    try {
      const result = await runScreeningMutation.mutateAsync({
        name: formData.name,
        country: formData.country || undefined,
        date_of_birth: formData.date_of_birth || undefined,
        check_types: ['sanctions', 'pep', 'adverse_media']
      });

      setShowNewCheck(false);
      setFormData({ entity_type: 'individual', name: '', country: '', date_of_birth: '' });

      if (result.hit_count > 0) {
        toast.warning(`${result.hit_count} potential match${result.hit_count > 1 ? 'es' : ''} found`);
      } else {
        toast.success('Screening clear - no matches found');
      }
    } catch (err) {
      toast.error(`Screening failed: ${err.message}`);
    }
  };

  // Handle resolving a hit
  const handleResolveHit = async (hitId, resolution) => {
    try {
      await resolveHitMutation.mutateAsync({
        hitId,
        resolution,
        checkId: selectedCheck?.id
      });
      toast.success(`Hit marked as ${resolution === 'confirmed_false' ? 'clear' : 'true match'}`);
      // Refresh the selected check data
      refetch();
    } catch (err) {
      toast.error(`Resolution failed: ${err.message}`);
    }
  };

  return (
    <div className="max-w-[1400px]">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight">AML Screening</h1>
          <p className="text-grey-500 text-sm mt-1">Sanctions, PEP, and adverse media screening checks</p>
        </div>

        <div className="flex gap-3">
          <Button onClick={() => setShowNewCheck(true)}>
            <Play size={16} />
            Run New Check
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-[13px] text-grey-500 mb-2">Pending Review</div>
          <div className="text-[32px] font-bold tracking-tight text-orange-500">{stats.pendingReview}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[13px] text-grey-500 mb-2">Total Hits (30d)</div>
          <div className="text-[32px] font-bold tracking-tight">{stats.totalHits}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[13px] text-grey-500 mb-2">True Positives</div>
          <div className="text-[32px] font-bold tracking-tight text-red-500">{stats.truePositives}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[13px] text-grey-500 mb-2">Checks Today</div>
          <div className="text-[32px] font-bold tracking-tight text-green-500">{stats.checksToday}</div>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-t-xl p-4">
        {[
          { key: 'all', label: 'All Checks', count: data?.total || 0 },
          { key: 'hits', label: 'With Hits', count: stats.totalHits },
          { key: 'pending', label: 'Pending Review', count: stats.pendingReview },
        ].map(tab => (
          <button
            key={tab.key}
            className={cn(
              'py-2 px-4 rounded-lg text-sm font-medium text-grey-500 transition-all flex items-center gap-2 cursor-pointer',
              'hover:bg-grey-100 dark:hover:bg-grey-800 hover:text-grey-900 dark:hover:text-grey-50',
              activeFilter === tab.key && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400'
            )}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label}
            <span className={cn(
              'px-2 py-0.5 rounded-full text-xs',
              activeFilter === tab.key ? 'bg-teal-500 text-white' : 'bg-grey-100 dark:bg-grey-800'
            )}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <Card className="border-t-0 rounded-t-none">
          <ScreeningSkeleton />
        </Card>
      ) : error ? (
        <ErrorState message={error.message} onRetry={refetch} />
      ) : checks.length === 0 ? (
        <Card className="border-t-0 rounded-t-none py-16 text-center">
          <Shield size={48} className="text-grey-300 mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No Screening Checks Found</h3>
          <p className="text-grey-500 mb-5">
            {activeFilter === 'all'
              ? 'Run your first screening check to get started.'
              : `No checks match the "${activeFilter}" filter.`}
          </p>
          <Button onClick={() => setShowNewCheck(true)}>
            <Play size={14} />
            Run New Check
          </Button>
        </Card>
      ) : (
        <Card className="border-t-0 rounded-t-none overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-grey-50 dark:bg-grey-800">
                <th className="text-left py-3 px-4 text-xs font-semibold text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Entity</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Check Status</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Hits</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Match Status</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Created</th>
                <th className="w-20 border-b border-grey-200 dark:border-grey-700"></th>
              </tr>
            </thead>
            <tbody>
              {checks.map((check) => {
                // Determine match status from hits
                const hasUnresolvedHits = check.hits?.some(h => h.resolution_status === 'pending');
                const hasConfirmedTrue = check.hits?.some(h => h.resolution_status === 'confirmed_true');
                const matchStatusKey = hasUnresolvedHits
                  ? 'pending_review'
                  : hasConfirmedTrue
                  ? 'confirmed_true'
                  : check.status === 'hit'
                  ? 'confirmed_clear'
                  : 'clear';
                const matchStatus = statusConfig[matchStatusKey];

                return (
                  <tr
                    key={check.id}
                    className="border-b border-grey-100 dark:border-grey-800 last:border-b-0 hover:bg-grey-50 dark:hover:bg-grey-800/50 cursor-pointer"
                    onClick={() => setSelectedCheck(check)}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-grey-100 dark:bg-grey-800 flex items-center justify-center text-grey-500">
                          {check.entity_type === 'wallet' ? <Wallet size={18} /> : check.entity_type === 'individual' ? <User size={18} /> : <Building2 size={18} />}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">
                            {check.entity_type === 'wallet'
                              ? `${check.screened_name.slice(0, 6)}...${check.screened_name.slice(-4)}`
                              : check.screened_name}
                          </span>
                          <span className="text-xs text-grey-400 flex items-center gap-1.5">
                            {check.entity_type === 'wallet' ? (
                              <>
                                <span className="capitalize">{check.wallet_network || 'ethereum'}</span>
                                <span>•</span>
                                Chainalysis
                              </>
                            ) : (
                              <>
                                {countryToFlag(check.screened_country)} {countryCodeToName[check.screened_country] || check.screened_country || 'Unknown'}
                                <span>•</span>
                                {check.entity_type || 'individual'}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {check.status === 'hit' ? (
                        <Badge variant="warning">
                          <AlertTriangle size={12} />
                          Hit
                        </Badge>
                      ) : check.status === 'error' ? (
                        <Badge variant="destructive">
                          <XCircle size={12} />
                          Error
                        </Badge>
                      ) : (
                        <Badge variant="success">
                          <CheckCircle2 size={12} />
                          Clear
                        </Badge>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className={cn('flex items-center gap-1.5 text-sm', check.hit_count > 0 ? 'text-orange-500' : 'text-green-500')}>
                        {check.hit_count > 0 ? (
                          <>
                            <AlertTriangle size={14} />
                            {check.hit_count} {check.hit_count === 1 ? 'hit' : 'hits'}
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={14} />
                            No hits
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <Badge variant={matchStatus?.color === 'warning' ? 'warning' : matchStatus?.color === 'destructive' ? 'destructive' : 'success'}>
                        {matchStatus?.label}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-grey-500 text-[13px]">
                      {formatDateTime(check.started_at || check.created_at)}
                    </td>
                    <td className="py-4 px-4">
                      <button className="w-8 h-8 border-none bg-transparent text-grey-400 cursor-pointer rounded-md hover:bg-grey-100 dark:hover:bg-grey-800 flex items-center justify-center">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* List Sources Card */}
      <Card className="mt-6">
        <div className="p-4 border-b border-grey-200 dark:border-grey-800 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">
            <Database size={16} />
            Connected List Sources
          </div>
          <Button variant="secondary" size="sm">
            <RefreshCw size={14} />
            Sync All
          </Button>
        </div>
        {listSources.length > 0 ? (
          listSources.map((source) => (
            <div key={source.id} className="flex items-center py-3 px-4 border-b border-grey-100 dark:border-grey-800 last:border-b-0">
              <span className="flex-1 font-medium text-sm">{source.name}</span>
              <span className="text-xs text-grey-400 font-mono mr-4">{source.version}</span>
              <span className="text-[13px] text-grey-500">{(source.entity_count || source.entityCount || 0).toLocaleString()} entities</span>
            </div>
          ))
        ) : (
          <div className="py-4 px-4 text-center text-grey-400 text-sm">
            Loading list sources...
          </div>
        )}
      </Card>

      {/* Detail Panel Overlay */}
      {selectedCheck && (
        <div
          className="fixed inset-0 bg-black/50 z-[150] transition-opacity"
          onClick={() => setSelectedCheck(null)}
        />
      )}

      {/* Detail Panel */}
      <div className={cn(
        'fixed right-0 top-0 bottom-0 w-[480px] bg-white dark:bg-grey-900 border-l border-grey-200 dark:border-grey-800 z-[200] overflow-y-auto transition-transform duration-300',
        selectedCheck ? 'translate-x-0' : 'translate-x-full'
      )}>
        {selectedCheck && (
          <>
            <div className="p-5 border-b border-grey-200 dark:border-grey-800 flex items-center justify-between sticky top-0 bg-white dark:bg-grey-900 z-10">
              <span className="text-lg font-semibold">Check Details</span>
              <button
                className="w-8 h-8 rounded-lg border-none bg-grey-100 dark:bg-grey-800 text-grey-500 cursor-pointer flex items-center justify-center"
                onClick={() => setSelectedCheck(null)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              {/* Entity Section */}
              <div className="mb-6">
                <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-3">Entity</div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-grey-100 dark:bg-grey-800 flex items-center justify-center text-grey-500">
                    {selectedCheck.entity_type === 'wallet' ? <Wallet size={18} /> : selectedCheck.entity_type === 'individual' ? <User size={18} /> : <Building2 size={18} />}
                  </div>
                  <div className="flex flex-col">
                    <span className={cn('font-medium', selectedCheck.entity_type === 'wallet' && 'font-mono text-[13px]')}>
                      {selectedCheck.entity_type === 'wallet'
                        ? `${selectedCheck.screened_name.slice(0, 10)}...${selectedCheck.screened_name.slice(-6)}`
                        : selectedCheck.screened_name}
                    </span>
                    <span className="text-xs text-grey-400">
                      {selectedCheck.entity_type === 'wallet' ? (
                        <>
                          <span className="capitalize">{selectedCheck.wallet_network || 'ethereum'}</span>
                          <span> • </span>
                          Chainalysis Sanctions Oracle
                        </>
                      ) : (
                        <>
                          {countryToFlag(selectedCheck.screened_country)} {countryCodeToName[selectedCheck.screened_country] || selectedCheck.screened_country || 'Unknown'}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                {selectedCheck.entity_type === 'wallet' && (
                  <div className="text-[13px] text-grey-500 font-mono break-all">
                    {selectedCheck.screened_name}
                  </div>
                )}
                {selectedCheck.screened_dob && selectedCheck.entity_type !== 'wallet' && (
                  <div className="text-[13px] text-grey-500 mt-2 flex items-center gap-1.5">
                    <Calendar size={14} />
                    DOB: {selectedCheck.screened_dob}
                  </div>
                )}
              </div>

              {/* Screening Hits Section */}
              {selectedCheck.hits?.length > 0 && (
                <div className="mb-6">
                  <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-3">
                    Screening Hits ({selectedCheck.hits.length})
                  </div>
                  {selectedCheck.hits.map((hit) => {
                    const confidencePercent = Math.round(hit.confidence || 0);
                    const isResolved = hit.resolution_status !== 'pending';

                    return (
                      <div key={hit.id} className="bg-grey-50 dark:bg-grey-800 rounded-lg p-4 mb-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-sm">{formatListSource(hit.list_source || hit.hit_type)}</span>
                          <div className="flex items-center gap-1.5 text-[13px]">
                            <div className="w-[60px] h-1.5 bg-grey-200 dark:bg-grey-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-teal-500 rounded-full"
                                style={{ width: `${confidencePercent}%` }}
                              />
                            </div>
                            <span>{confidencePercent}%</span>
                          </div>
                        </div>
                        <div className="text-[13px] text-grey-600 dark:text-grey-400 leading-snug mb-3">
                          <strong>{hit.matched_name}</strong>
                          {hit.pep_position && ` - ${hit.pep_position}`}
                          {hit.pep_tier && ` (Tier ${hit.pep_tier})`}
                        </div>
                        {hit.match_data?.category && (
                          <div className="text-xs text-grey-400 capitalize mb-1">
                            Category: {hit.match_data.category}
                          </div>
                        )}
                        {hit.match_data?.description && (
                          <div className="text-xs text-grey-400 mb-1">{hit.match_data.description}</div>
                        )}
                        {hit.match_data?.url && (
                          <a
                            href={hit.match_data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-teal-500 inline-flex items-center gap-1 no-underline hover:underline"
                          >
                            <ExternalLink size={12} /> More info
                          </a>
                        )}
                        <div className="text-xs text-grey-400 mt-2">
                          Source: {formatListSource(hit.list_source)} • {hit.list_version_id}
                        </div>
                        <div className="flex gap-1.5 flex-wrap mt-2">
                          {(hit.matched_fields || []).map((field, i) => (
                            <span key={i} className="px-2 py-0.5 bg-grey-100 dark:bg-grey-700 border border-grey-200 dark:border-grey-600 rounded text-[11px] font-medium text-grey-600 dark:text-grey-400">
                              {field}
                            </span>
                          ))}
                        </div>
                        {isResolved && (
                          <div className="mt-3 pt-3 border-t border-grey-200 dark:border-grey-700">
                            <Badge variant={hit.resolution_status === 'confirmed_false' ? 'success' : 'destructive'}>
                              {hit.resolution_status === 'confirmed_false' ? 'Cleared' : 'Confirmed Match'}
                            </Badge>
                            {hit.resolution_notes && (
                              <div className="text-xs text-grey-500 mt-2">{hit.resolution_notes}</div>
                            )}
                            {hit.resolved_at && (
                              <div className="text-xs text-grey-400 mt-1">
                                Resolved: {formatDateTime(hit.resolved_at)}
                              </div>
                            )}
                          </div>
                        )}
                        {!isResolved && (
                          <div className="mt-3">
                            <HitAISuggestion
                              hitId={hit.id}
                              onResolve={(resolution) => handleResolveHit(hit.id, resolution)}
                              isResolving={resolveHitMutation.isPending}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* No Matches State */}
              {selectedCheck.hits?.length === 0 && selectedCheck.status === 'clear' && (
                <div className="mb-6">
                  <div className="text-center py-10 bg-grey-50 dark:bg-grey-800 rounded-lg">
                    <CheckCircle2 size={48} className="text-green-500 mx-auto mb-3" />
                    <h4 className="font-semibold mb-2">No Matches Found</h4>
                    <p className="text-grey-500 text-sm">
                      This entity was screened against all connected list sources with no matches.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* New Check Modal */}
      <Modal isOpen={showNewCheck} onClose={() => setShowNewCheck(false)}>
        <form onSubmit={handleRunCheck}>
          <div className="p-5 border-b border-grey-200 dark:border-grey-800">
            <h2 className="text-lg font-semibold">Run New Screening Check</h2>
          </div>
          <div className="p-5">
            <div className="mb-5">
              <label className="block text-[13px] font-medium mb-2">Entity Type</label>
              <select
                className="w-full h-11 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg px-3.5 text-sm cursor-pointer outline-none focus:border-teal-500"
                value={formData.entity_type}
                onChange={(e) => setFormData(prev => ({ ...prev, entity_type: e.target.value }))}
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>
            <div className="mb-5">
              <label className="block text-[13px] font-medium mb-2">Full Name *</label>
              <input
                type="text"
                className="w-full h-11 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg px-3.5 text-sm outline-none focus:border-teal-500"
                placeholder="Enter full name..."
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="mb-5">
              <label className="block text-[13px] font-medium mb-2">Country</label>
              <select
                className="w-full h-11 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg px-3.5 text-sm cursor-pointer outline-none focus:border-teal-500"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              >
                <option value="">Select country...</option>
                <optgroup label="High-Risk Jurisdictions">
                  <option value="RU">Russia</option>
                  <option value="CN">China</option>
                  <option value="IR">Iran</option>
                  <option value="KP">North Korea</option>
                  <option value="SY">Syria</option>
                  <option value="CU">Cuba</option>
                  <option value="VE">Venezuela</option>
                  <option value="MM">Myanmar</option>
                  <option value="BY">Belarus</option>
                </optgroup>
                <optgroup label="Common Countries">
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="JP">Japan</option>
                </optgroup>
              </select>
            </div>
            <div className="mb-5">
              <label className="block text-[13px] font-medium mb-2">Date of Birth (optional)</label>
              <input
                type="date"
                className="w-full h-11 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg px-3.5 text-sm outline-none focus:border-teal-500"
                value={formData.date_of_birth}
                onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
              />
            </div>
          </div>
          <div className="p-4 border-t border-grey-200 dark:border-grey-800 flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowNewCheck(false);
                setFormData({ entity_type: 'individual', name: '', country: '', date_of_birth: '' });
              }}
              disabled={runScreeningMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={runScreeningMutation.isPending}>
              {runScreeningMutation.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={14} />
                  Run Check
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
